<template>
  <mce-editor
    type="freestyle-content"
    :tinymceScriptSrc="'https://d25510ev64x81w.cloudfront.net/js/tinymce/5.10.5/tinymce.min.js'"
    :class="classComponent"
    :id="blockId"
    :ref="blockId"
    :init="initOptions"
    :value="dataValue"
    :disabled="disabledEditor"
    :style="blockStyle"
    @onClick="onClick"
    @input="onInput"
    @onBlur="onBlur"
  />
</template>

<script>
import mceEditor from '@tinymce/tinymce-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import debounce from 'debounce'
import eventCategories from '@/constant/eventCategories'
import Socket from '@/socket'
import configs from '@/config'
import utils from '@/util'
import EventBus from '@/eventBus'
import ClickableMixin from '@/mixins/text-editor/ClickableMixin'

export default {
  components: {
    mceEditor,
  },
  mixins: [ClickableMixin],
  props: {
    value: {
      type: String,
      default: null,
    },
    slideId: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    blockId: {
      type: String,
      default: null,
    },
    blockStyle: {
      type: Object,
      default: null,
    },
  },
  data: () => {
    return {
      disabledEditor: false,
      mceEditorKey: configs.mceEditorKey,
      isInputOnFocus: false,
      mceEditorInstance: null,
      lastCaret: null,
      blurAfterMount: false,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
    classComponent() {
      return {
        'element-editor': true,
        'rich-text-editor': true,
        'is-focused': this.isInputOnFocus,
        'mce-edit-focus': this.isInputOnFocus,
        'is-disabled': this.disabled,
        'light-text-colour': this.isTextColourLight,
        'is-sharing': this.sharePresentation,
        clickable: this.clickable,
      }
    },
    isPresentingOrBackstageOrShare() {
      return this.sharePresentation || this.isPresenting || this.isBackstage
    },
    isDisableEditor() {
      return this.isDisableEditPresentation || this.isPresentingOrBackstageOrShare
    },
    isTextColourLight() {
      return this.slideActive.textColour === '#ffffff'
    },
  },

  watch: {
    isPresenting: function watchIsPresenting() {
      if (!this.isPresenting) this._setInputBlur()
    },
    disabled: {
      handler(value, oldValue) {
        if (value !== oldValue) {
          this.disabledEditor = value
        }
      },
      immediate: false,
    },
  },

  created() {
    const self = this

    this.initOptions = {
      relative_urls: false,
      remove_script_host: false,
      menubar: false,
      inline: true,
      readonly: true,
      fixed_toolbar_container: '.freestyle-editor-toolbar',
      plugins: [
        'advlist autolink lists link charmap',
        'paste wordcount textpattern',
        // 'tiny_mce_wiris', // -> Math plugin, will be developed in Sprint 49
      ],
      // external_plugins: { // -> Math plugin, will be developed in Sprint 49
      //   tiny_mce_wiris: 'https://d25510ev64x81w.cloudfront.net/js/mathtype-plugin/plugin.min.js',
      // },
      // To enable mathtype, add tiny_mce_wiris_formulaEditor to the toolbar

      toolbar: `
        fontselect | fontsizeselect | bold italic underline strikethrough |
        forecolor backcolor |
        alignleft aligncenter alignright alignjustify |
        bullist numlist link charmap
      `,
      fontsize_formats: '12=16px 13=20px 14=24px 15=28px 16=32px 18=36px 20=40px 22=44px 24=48px 28=52px 32=56px 40=64px 50=70px 60=90px 70=110px 80=125px 90=140px',
      font_formats: 'Nunito=Nunito; Roboto=Roboto; Roboto Slab=Roboto Slab; Open Sans=Open Sans; Oswald=Oswald; Montserrat=Montserrat; Source Code Pro=Source Code Pro; Merriweather=Merriweather; EB Garamond=EB Garamond; Cairo=Cairo; Kanit=Kanit',
      default_link_target: '_blank',
      paste_data_images: false,
      paste_as_text: false,
      remove_linebreaks: false,
      textpattern_patterns: [
        { start: '#', format: 'h1' },
        { start: '##', format: 'h2' },
        { start: '###', format: 'h3' },
        { start: '####', format: 'h4' },
        { start: '#####', format: 'h5' },
        { start: '######', format: 'h6' },
        // The following text patterns require the `lists` plugin
        { start: '1. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'decimal' } },
        { start: '1) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'decimal' } },
        { start: 'a. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-alpha' } },
        { start: 'a) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-alpha' } },
        { start: 'A. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'upper-alpha' } },
        { start: 'A) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'upper-alpha' } },
        { start: 'i. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-roman' } },
        { start: 'i) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-roman' } },
        { start: '* ', cmd: 'InsertUnorderedList' },
        { start: '- ', cmd: 'InsertUnorderedList' },
        { start: '**', end: '**', format: 'bold' },
        { start: '(c)', replacement: '©' },
      ],

      setup(editor) {
        self.mceEditorInstance = editor
        editor.on('ExecCommand', (e) => {
          self.handleTrackEditing(e.command, e.value)
          const cmd = e.command
          // Workaround to edit font size for list marker equals to list content
          if (cmd === 'FontSize') {
            const fontSize = e.value
            const fontSizeNumber = e.value.replace(/em|px|rem|pt/g, '')
            const node = e.target.selection.getNode()
            const nodeParent = node.parentNode
            if (node.nodeName === 'SPAN' && nodeParent.nodeName === 'LI') {
              editor.dom.setStyle(nodeParent, 'font-size', fontSize)
            } else if (node.nodeName === 'UL' || node.nodeName === 'OL') {
              const li = editor.dom.select('li', node)
              editor.dom.setStyle(li, 'font-size', fontSize)
              editor.dom.setStyle(node, 'padding-left', `${fontSizeNumber * 2 < 60 ? 60 : fontSizeNumber * 2}px`)
            }
          }
        })

        editor.on('init', () => {
          self.$emit('ready', self.slideId)
          self.anchorClickableLink(self.value)
          if (self.isDisableEditor) {
            self.disabledEditor = true
          }
        })
        editor.on('paste', (e) => {
          const paste = (e.clipboardData || window.clipboardData).getData('text')
          // Prevent paste images from other source
          // Only allow pasting text
          if (!paste) {
            e.preventDefault()
          }
        })

        // AHA-19070 Prevent unexpected errors because we don't listen the 'focus' event
        // We only listen the 'click' event to control the state of the RichTextEditor
        editor.off('focus')
      },

    }
  },

  mounted() {
    EventBus.$on(`focus-editor-${this.blockId}`, () => {
      EventBus.$emit('show-freestyle-editor-toolbar', true)
      this.onFocus()
      this.mceEditorInstance.selection.select(this.mceEditorInstance.getBody(), true)
    })
  },

  beforeDestroy() {
    if (this.sharePresentation || !this.presentation.id) return
    this.onBlur()
  },

  destroyed() {
    EventBus.$off(`focus-editor-${this.blockId}`)
  },

  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction', 'blockHistoryAction', 'unblockHistoryAction']),
    onChangeEditorValue: debounce(function debounceChangeEditorValue() {
      this.onUpdateBodyContent()
      this.clearBlockContentCache(this.blockId)
    }, 500, { leading: true }),

    onInputEditor: debounce(function debounceInput() {
      const editor = this.getValidatedInput()
      if (!editor ||
            editor.innerHTML === this.value) return
      this.cacheBlockContent(this.blockId, editor.innerHTML)
    }, 300),

    cacheBlockContent(blockId, blockContent) {
      if (!this.$store.state.blockContentCache) {
        this.$store.state.blockContentCache = {}
      }
      this.$store.state.blockContentCache[blockId] = blockContent
    },
    clearBlockContentCache(blockId) {
      if (this.$store.state.blockContentCache && this.$store.state.blockContentCache[blockId]) {
        delete this.$store.state.blockContentCache[blockId]
      }
    },

    getValidatedInput() {
      const editor = document.getElementById(this.blockId)
      if (!editor) return null
      if (editor.innerHTML === this.valueHtml) return null
      return editor
    },

    async onUpdateBodyContent() {
      try {
        const editor = this.getValidatedInput()
        if (!editor ||
            editor.innerHTML === this.value) return
        this.setSyncLoadingAction(1)
        this.updateValueBlockEditor(editor.innerHTML)
        this.setSyncLoadingAction(2)
        this._handleTrackGAAfterUpdate()
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },

    updateValueBlockEditor(value) {
      this.$emit('rich-text-editor-update', value)
    },

    onBlur() {
      this.unblockHistoryAction()
      this.isInputOnFocus = false
      this.lastCaret = null
      window.getSelection().empty()
      this.onChangeEditorValue()
      setTimeout(() => {
        this.$emit('onBlur')
        EventBus.$emit('blur-editing-box')
      }, 100)
      if (this.blurAfterMount) {
        this.blurAfterMount = false
      }
    },

    _setInputBlur(whenInit = false) {
      setTimeout(() => {
        const editor = document.getElementById(this.blockId)
        if (editor) {
          this.blurAfterMount = whenInit
          editor.blur()
        }
      }, 0)
    },

    onClick(e) {
      // Only accept 'click' event trigger from MoveableBlockEditor to focus the editor
      if (!e.isTrusted) {
        this.onFocus()
        this.mceEditorInstance.focus()
      }
    },

    onInput() {
      this.onInputEditor()
    },

    onFocus() {
      this.blockHistoryAction()
      const editor = document.getElementById(this.blockId)
      if (!editor) return
      this.isInputOnFocus = true
      this.$emit('onFocusEditor')

      if (!this.value ||
        utils.extractContent(this.value, true).includes('Click to add') ||
        utils.extractContent(this.value, true).includes('Replace this with')
      ) {
        this.mceEditorInstance.selection.select(this.mceEditorInstance.getBody(), true)
      }

      // Nếu chưa focus và không phải đang bôi đen thì sẽ cho con trỏ về cuối.
      if (!this.mceEditorInstance.selection.getContent().length && !this.lastCaret) {
        this.mceEditorInstance.selection.select(this.mceEditorInstance.getBody(), true)
        this.mceEditorInstance.selection.collapse(false)
      }
      this.lastCaret = this.mceEditorInstance.selection.getSel().focusOffset
    },

    async imageUploadHandler(file, success, failure) {
      const blob = file.blob()
      const MAX_IMAGE_SIZE = 15 * 1024 * 1024 // 15MB
      if (blob.size > MAX_IMAGE_SIZE) {
        failure('Maximum file size of an image is 15MB. Try different image!', {
          remove: true,
        })
        return
      }

      const formData = new FormData()

      formData.append('file', blob)
      formData.append('accessCode', this.presentation.uniqueAccessCode)
      formData.append('socketId', Socket.getSocketId())

      try {
        this.$store.state.imageUploading = true
        const res = await this.$http.post(`${configs.api.publicUpload}/image`, formData, {
          timeout: 60000,
        })
        success(res.body.key)
        setTimeout(() => {
          // Update lại HTML mới nhất khi upload image success, tránh trường hợp presenter blur ra khỏi editor khi chưa upload image xong (lúc này thì url có dạng blob:https://...)
          if (!this.isInputOnFocus) {
            this.onUpdateBodyContent()
          }
          this.$store.state.imageUploading = false
        }, 10)
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Edit slide body > Insert image',
          '',
          this.presentation.id,
          {
            slideId: this.slideActive.id,
          },
        )
      } catch (error) {
        failure(error)
      }
    },

    handleTrackEditing(command, value) {
      const trackableCommands = [
        'mceApplyTextColor',
        'FontSize',
        'FontName',
        'mceToggleFormat',
        'mceApplyTextcolor',
        'JustifyCenter',
        'JustifyFull',
        'JustifyLeft',
        'JustifyRight',
        'InsertOrderedList',
        'InsertUnorderedList',
        'mceInsertLink',
        'mceShowCharmap',
      ]
      if (!trackableCommands.includes(command)) return
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        `Edit slide body > ${command}`,
        value,
        this.presentation.id,
        {
          slideId: this.slideActive.id,
        },
      )
    },

    _handleTrackGAAfterUpdate() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Edit slide body > Edit text',
        '',
        this.presentation.id,
        {
          slideId: this.slideActive.id,
        },
      )
    },

    eventsDistributor(evtName, e) {
      console.log(evtName, e)
    },

  },

}

</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.rich-text-editor {
  overflow: hidden;
  width: 100%;
  font-size: 2rem;
  padding: 10px;
  min-height: 42px;
  min-width: 42px;
  -webkit-user-select: text; // Fix bug: editor doesn't work on Safari
  cursor: text;
  line-height: 1.2;
  &.is-disabled {
    border: none;
    cursor: default;
  }
  &:hover {
    overflow-y: auto;
  }

  &::v-deep .mce-clonedresizable {
    position: absolute !important;
  }
  &::v-deep .mce-resize-helper {
    display: none !important;
  }
  &::v-deep img {
    outline: none !important;
  }

}
.clickable {
      ::v-deep .text-hyper-link{
        &:hover{
          text-decoration: underline !important;
        }
        text-decoration: underline;
        pointer-events: inherit;
        color: inherit;
      }
    }
.slide-container {

  .freestyle-canvas-content {

    .is-sharing.mce-content-body {
      ::v-deep [data-mce-selected="inline-boundary"]{
        background-color: inherit;
      }
    }
  }
}

</style>
